import React, { useId, useMemo, useRef } from "react";
import { CalendarDays, Clock, ChevronDown, X } from "lucide-react";
import CustomFormField, { getFieldIds } from "../CustomFormField/CustomFormField";
import "../CustomTheme/customTheme.css";
import "./CustomDatePicker.css";

const NATIVE_TYPE = {
  date: "date",
  datetime: "datetime-local",
  month: "month",
  time: "time",
};

/**
 * CustomDatePicker — one component for every date-like field.
 *
 * Props:
 *  - type      "date" (default) | "datetime" | "month" | "time" | "year"
 *  - value     string in the native format:
 *                date "YYYY-MM-DD" · datetime "YYYY-MM-DDTHH:mm" ·
 *                month "YYYY-MM" · time "HH:mm" · year "YYYY"
 *  - onChange(value) | setInputText(value)   ("" when cleared)
 *  - minDate / maxDate   same format as value (old names mindate / maxdate also work)
 *  - minYear / maxYear   range for type="year" (default: current year −10 … +5)
 *  - clearable           shows an × to empty the field (default true)
 *  - label | title, name, id, isRequired, hint, error, colClass, noMargin, disabled, placeholder
 */
const CustomDatePicker = ({
  type = "date",
  label,
  title,
  name,
  id,
  isRequired,
  hint,
  error,
  colClass,
  noMargin = false,
  value,
  onChange,
  setInputText,
  minDate,
  maxDate,
  mindate, // old prop names
  maxdate,
  minYear,
  maxYear,
  clearable = true,
  disabled = false,
  placeholder,
  className,
  style,
}) => {
  const autoId = useId();
  const inputId = id || name || `cdp-${autoId}`;
  const ids = getFieldIds(inputId);
  const inputRef = useRef(null);

  const min = minDate ?? mindate;
  const max = maxDate ?? maxdate;
  const current = value ?? "";
  const hasValue = current !== "" && current !== null;

  const emit = (next) => {
    onChange?.(next);
    setInputText?.(next);
  };

  const years = useMemo(() => {
    if (type !== "year") return [];
    const now = new Date().getFullYear();
    const from = Number(minYear ?? (min ? String(min).slice(0, 4) : now - 10));
    const to = Number(maxYear ?? (max ? String(max).slice(0, 4) : now + 5));
    const list = [];
    for (let y = to; y >= from; y -= 1) list.push(String(y));
    return list;
  }, [type, minYear, maxYear, min, max]);

  // Open the native picker when the field itself is clicked (Chrome/Edge/Safari 16+)
  const openPicker = () => {
    if (disabled) return;
    try {
      inputRef.current?.showPicker?.();
    } catch (_) {
      /* browser blocked it — the native control still works on its own */
    }
  };

  const controlClass = `cui-control cdp-control${error ? " cui-control-error" : ""}${
    hasValue ? "" : " cdp-empty"
  }${className ? ` ${className}` : ""}`;

  const shared = {
    id: inputId,
    name,
    disabled,
    "aria-invalid": Boolean(error),
    "aria-required": Boolean(isRequired),
    "aria-describedby": ids.describedBy(error, hint),
    className: controlClass,
    style,
  };

  const Icon = type === "time" ? Clock : type === "year" ? ChevronDown : CalendarDays;

  return (
    <CustomFormField
      label={label ?? title}
      isRequired={isRequired}
      htmlFor={inputId}
      hint={hint}
      error={error}
      colClass={colClass}
      noMargin={noMargin}
    >
      <div className={`cdp-wrap${clearable && hasValue && !disabled ? " cdp-wrap-clearable" : ""}`}>
        {type === "year" ? (
          <select {...shared} value={current} onChange={(e) => emit(e.target.value)}>
            <option value="">{placeholder || "Select year"}</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        ) : (
          <input
            {...shared}
            ref={inputRef}
            type={NATIVE_TYPE[type] || "date"}
            value={current}
            min={min}
            max={max}
            placeholder={placeholder}
            onChange={(e) => emit(e.target.value)}
            onClick={openPicker}
          />
        )}

        {clearable && hasValue && !disabled && (
          <button
            type="button"
            className="cdp-clear"
            onClick={() => emit("")}
            aria-label="Clear"
            title="Clear"
          >
            <X size={14} />
          </button>
        )}

        <span className="cdp-icon" aria-hidden="true">
          <Icon size={16} />
        </span>
      </div>
    </CustomFormField>
  );
};

export default CustomDatePicker;
